/**
 * Pure helpers for policy v2 in the console: how a setting's value and source read, the
 * settings form <-> a layer, freeze windows in words, and version diffs in words.
 */
import { APPROVAL_AUTONOMY_LABEL } from '@orgus/protocol';
import type { ApprovalRequest, EffectiveSetting, FreezeWindow, FreezeWindowInput, Id, LandingReviewer, PolicyDiffEntry, PolicyRule, PolicySettings, SettingSource } from '@orgus/protocol';
import type { RuleForm } from '../approvals/policyModel.ts';

export const SOURCE_LABEL: Record<SettingSource, string> = {
  default: 'Built in',
  org: 'Every project',
  project: 'This project',
  session: 'This session',
};

export const REVIEWER_LABEL: Record<LandingReviewer, string> = {
  none: 'No review',
  human: 'A person',
  agent: 'A review agent',
  'human-or-agent': 'A person or a review agent',
};

export const MODE_LABEL: Record<string, string> = {
  default: 'Ask as usual',
  acceptEdits: 'Accept edits',
  plan: 'Plan only',
  dontAsk: 'Do not ask',
  bypassPermissions: 'Bypass permissions',
};

export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "10 min", "1 h", "Never": how long an idle session stays open. */
export function idleCloseLabel(minutes: number): string {
  if (minutes <= 0) return 'Never';
  return minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`;
}

/** The few choices the editor offers; a stored value off this list is shown as its own choice. */
export const IDLE_CLOSE_PRESETS = [10, 30, 60, 0] as const;

/** A setting's value in words: lists joined, booleans as Yes and No, unset as Not set. */
export function formatValue(key: EffectiveSetting['key'], v: unknown): string {
  if (v === undefined || v === null || v === '') return key === 'model' ? 'Claude Code default' : key === 'allowedModels' ? 'Any model' : 'Not set';
  if (key === 'permissionMode' && typeof v === 'string') return MODE_LABEL[v] ?? v;
  if (key === 'reviewer' && typeof v === 'string') return REVIEWER_LABEL[v as LandingReviewer] ?? v;
  if (key === 'autonomy' && (v === 'full' || v === 'ask')) return APPROVAL_AUTONOMY_LABEL[v];
  if (key === 'idleCloseMinutes' && typeof v === 'number') return idleCloseLabel(v);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.join(', ') : 'None';
  if (typeof v === 'object') {
    const e = Object.entries(v as Record<string, unknown>);
    return e.length ? e.map(([k, x]) => `${k}=${String(x)}`).join(', ') : 'None';
  }
  return String(v);
}

/** "This project, replacing Every project (sonnet)". */
export function describeSource(s: EffectiveSetting): string {
  const base = SOURCE_LABEL[s.source];
  const prev = s.overridden[0];
  return prev ? `${base}, replacing ${SOURCE_LABEL[prev.source]} (${formatValue(s.key, prev.value)})` : base;
}

/** The editor's fields for one layer. Empty strings and 'inherit' mean "not set here". */
export interface SettingsForm {
  defaultModel: string;
  allowedModels: string;
  permissionMode: string;
  disableBypassPermissions: 'inherit' | 'yes' | 'no';
  /** ADR 0070: full allows what only the floor or a built-in default would ask. */
  autonomy: 'inherit' | 'full' | 'ask';
  /** R-FN-6: minutes a launched session may sit idle before Orgus closes it; '0' never, 'inherit' the layer below. */
  idleClose: string;
  secrets: string[] | null;
  env: string;
  requiredChecks: string;
  reviewer: string;
}

const list = (s: string) =>
  s
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean);

export function settingsFormOf(s: PolicySettings | undefined): SettingsForm {
  const v = s ?? {};
  return {
    defaultModel: v.defaultModel ?? '',
    allowedModels: v.allowedModels?.join(', ') ?? '',
    permissionMode: v.permissionMode ?? 'inherit',
    disableBypassPermissions: v.disableBypassPermissions === undefined ? 'inherit' : v.disableBypassPermissions ? 'yes' : 'no',
    autonomy: v.autonomy ?? 'inherit',
    idleClose: v.idleCloseMinutes === undefined ? 'inherit' : String(v.idleCloseMinutes),
    secrets: v.secrets ?? null,
    env: Object.entries(v.env ?? {})
      .map(([k, x]) => `${k}=${x}`)
      .join('\n'),
    requiredChecks: v.requiredChecks?.join(', ') ?? '',
    reviewer: v.reviewer ?? 'inherit',
  };
}

/** The layer to save, or what is wrong in plain words. */
export function settingsOf(f: SettingsForm): { ok: true; settings: PolicySettings } | { ok: false; error: string } {
  const out: PolicySettings = {};
  if (f.defaultModel.trim()) out.defaultModel = f.defaultModel.trim();
  if (f.allowedModels.trim()) out.allowedModels = list(f.allowedModels);
  if (f.permissionMode && f.permissionMode !== 'inherit') out.permissionMode = f.permissionMode;
  if (f.disableBypassPermissions !== 'inherit') out.disableBypassPermissions = f.disableBypassPermissions === 'yes';
  if (f.autonomy !== 'inherit') out.autonomy = f.autonomy;
  if (f.idleClose !== 'inherit') {
    const n = Number(f.idleClose);
    if (!Number.isInteger(n) || n < 0) return { ok: false, error: 'Close idle sessions after takes whole minutes, or Never.' };
    out.idleCloseMinutes = n;
  }
  if (f.secrets) out.secrets = [...f.secrets];
  if (f.env.trim()) {
    const env: Record<string, string> = {};
    for (const line of f.env.split(/\r?\n/)) {
      const t = line.trim();
      if (!t || t.startsWith('#')) continue;
      const i = t.indexOf('=');
      if (i <= 0) return { ok: false, error: `"${t}" is not NAME=value.` };
      env[t.slice(0, i).trim()] = t.slice(i + 1).trim();
    }
    out.env = env;
  }
  if (f.requiredChecks.trim()) out.requiredChecks = list(f.requiredChecks);
  if (f.reviewer && f.reviewer !== 'inherit') out.reviewer = f.reviewer as LandingReviewer;
  if (out.defaultModel && out.allowedModels && !out.allowedModels.some((m) => out.defaultModel!.toLowerCase().includes(m.replace(/\*/g, '').toLowerCase()))) {
    return { ok: false, error: `The default model ${out.defaultModel} is not in the allowed models.` };
  }
  return { ok: true, settings: out };
}

/** "Weekly, Fri and Sat 18:00 to 23:00" or "17 Oct 10:00 to 17 Oct 12:00". */
export function describeFreeze(w: Pick<FreezeWindow, 'kind' | 'from' | 'to' | 'days' | 'start' | 'end'>, fmt: (ts: number) => string = (ts) => new Date(ts).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })): string {
  if (w.kind === 'once') return w.from != null && w.to != null ? `${fmt(w.from)} to ${fmt(w.to)}` : 'Once';
  const days = (w.days ?? []).slice().sort();
  const dayText = days.length === 7 ? 'Every day' : days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d)) ? 'Weekdays' : days.map((d) => DAY_SHORT[d]).join(', ');
  return `Weekly, ${dayText} ${w.start ?? ''} to ${w.end ?? ''}${w.end && w.start && w.end <= w.start ? ' (next day)' : ''}`;
}

/** The freeze editor's fields. */
export interface FreezeForm {
  name: string;
  scope: string;
  kind: 'once' | 'weekly';
  from: string;
  to: string;
  days: number[];
  start: string;
  end: string;
  blocks: FreezeWindow['blocks'];
  reason: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
/** A timestamp as a datetime-local value in this browser's time zone. */
export function toLocalInput(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function emptyFreezeForm(now: number): FreezeForm {
  return { name: '', scope: 'org', kind: 'once', from: toLocalInput(now), to: toLocalInput(now + 2 * 3_600_000), days: [5], start: '18:00', end: '08:00', blocks: ['landing', 'deploy'], reason: '' };
}

export function freezeFormOf(w: FreezeWindow, now: number): FreezeForm {
  return {
    name: w.name,
    scope: w.scope.kind === 'org' ? 'org' : w.scope.projectId,
    kind: w.kind,
    from: toLocalInput(w.from ?? now),
    to: toLocalInput(w.to ?? now + 3_600_000),
    days: w.days ?? [],
    start: w.start ?? '18:00',
    end: w.end ?? '08:00',
    blocks: w.blocks,
    reason: w.reason ?? '',
  };
}

export function freezeInputOf(f: FreezeForm, utcOffsetMinutes: number): { ok: true; input: FreezeWindowInput } | { ok: false; error: string } {
  if (!f.name.trim()) return { ok: false, error: 'Name the window, for example Release week.' };
  if (!f.blocks.length) return { ok: false, error: 'Pick what the window stops.' };
  const scope = f.scope === 'org' ? ({ kind: 'org' } as const) : ({ kind: 'project', projectId: f.scope } as const);
  const base = { name: f.name.trim(), scope, blocks: f.blocks, ...(f.reason.trim() ? { reason: f.reason.trim() } : {}) };
  if (f.kind === 'once') {
    const from = new Date(f.from).getTime();
    const to = new Date(f.to).getTime();
    if (!Number.isFinite(from) || !Number.isFinite(to)) return { ok: false, error: 'Pick a start and an end.' };
    if (to <= from) return { ok: false, error: 'The end must be after the start.' };
    return { ok: true, input: { ...base, kind: 'once', from, to } };
  }
  if (!f.days.length) return { ok: false, error: 'Pick at least one day.' };
  if (f.start === f.end) return { ok: false, error: 'The end time must differ from the start time.' };
  return { ok: true, input: { ...base, kind: 'weekly', days: [...f.days].sort(), start: f.start, end: f.end, utcOffsetMinutes } };
}

/** One diff entry in words: "Default model: sonnet to haiku". */
export function describeDiff(e: PolicyDiffEntry, projectName: (id: Id) => string | undefined): { title: string; detail: string; where: string } {
  const where = e.projectId ? (projectName(e.projectId) ?? 'Unknown project') : 'Every project';
  if (e.area === 'setting') {
    const key = e.id.split('.').pop() as EffectiveSetting['key'];
    const b = formatValue(key, e.before);
    const a = formatValue(key, e.after);
    return { title: e.label, detail: e.change === 'added' ? `Set to ${a}` : e.change === 'removed' ? `Unset (was ${b})` : `${b} to ${a}`, where };
  }
  if (e.area === 'rule') {
    const r = (e.after ?? e.before) as PolicyRule | undefined;
    const before = e.before as PolicyRule | undefined;
    const after = e.after as PolicyRule | undefined;
    const detail =
      e.change === 'changed' && before && after
        ? [before.decision !== after.decision ? `${before.decision} to ${after.decision}` : '', (before.enabled !== false) !== (after.enabled !== false) ? (after.enabled === false ? 'turned off' : 'turned on') : '', before.match.pattern !== after.match.pattern ? `pattern ${before.match.pattern ?? 'any'} to ${after.match.pattern ?? 'any'}` : '']
            .filter(Boolean)
            .join(', ') || 'Reason or details changed'
        : (r?.reason ?? '');
    return { title: e.label, detail, where };
  }
  return { title: e.label, detail: e.change === 'added' ? 'Added' : e.change === 'removed' ? 'Deleted' : 'Changed', where };
}

export const CHANGE_LABEL: Record<PolicyDiffEntry['change'], string> = { added: 'Added', removed: 'Removed', changed: 'Changed' };

/** "Make it a rule": the rule editor's fields pre-filled from an approval request. */
export function ruleFormFromApproval(a: Pick<ApprovalRequest, 'tool' | 'suggestedPattern' | 'subject' | 'projectId' | 'title'>): Partial<RuleForm> {
  return {
    scope: a.projectId ?? 'org',
    kind: 'tool',
    tool: a.tool ?? '',
    pattern: a.suggestedPattern ?? a.subject ?? '',
    decision: 'allow',
    reason: `Made from an approval: ${a.title}`.slice(0, 300),
  };
}
